import * as React from 'react';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { getTodoFolderById, getTodoFoldersLoadState } from 'owa-todo-folders';
import { isTaskComplete } from 'owa-todo-utils';
import {
    AssetErrorType,
    usePatchPayload,
    getProjectTasksParentFolderId,
    useProjectId,
    useIsProjectPreview,
} from 'owa-timestream-asset-sdk';
import {
    todoCache,
    createFolderIdDescriptor,
    initializeTasks,
} from 'owa-timestream-asset-sdk-tasks';
import useCreateTask from '../hooks/useCreateTask';
import type { TaskViewPayload } from '../schema';

export default function useTaskViewState(
    { taskId, taskParentId, taskSubject, isComplete }: TaskViewPayload,
    isDragPreview?: boolean
) {
    const projectId = useProjectId();
    const isPreview = useIsProjectPreview();
    const parentFolderId =
        taskParentId ?? (!isDragPreview ? getProjectTasksParentFolderId(projectId) : '');
    const folderIdDescriptor = createFolderIdDescriptor(parentFolderId);
    const task = todoCache.getTaskForLock(taskId, folderIdDescriptor);
    const taskFolder = getTodoFolderById(parentFolderId);
    const loadState = todoCache.getTasksLoadState(folderIdDescriptor);
    const folderLoadState = getTodoFoldersLoadState();

    const patchPayload = usePatchPayload<TaskViewPayload>();
    const createTask = useCreateTask();
    const [shouldDisableSubtasks, disableSubtasks, enableSubtasks] = useBooleanState(
        !task?.Subject
    );
    const [errorType, setErrorType] = React.useState<AssetErrorType | undefined>(undefined);

    const hasLocalTaskSubject = React.useCallback(
        (subject: string) => (!!subject ? enableSubtasks() : disableSubtasks()),
        []
    );

    // create task if there is no pre-existing task info besides the task subject
    React.useEffect(() => {
        if (!parentFolderId && !taskId && !!taskSubject && !isPreview) {
            createTask(parentFolderId, taskSubject);
        }
    }, [parentFolderId, taskId, taskSubject, isPreview]);

    // update task item if changed externally
    React.useEffect(() => {
        if (task && (taskSubject !== task.Subject || isTaskComplete(task.Status) !== isComplete)) {
            patchPayload({
                taskId,
                taskSubject: task.Subject,
                isComplete: isTaskComplete(task.Status),
            });
        }
    }, [taskId, task?.Subject, task?.Status, taskSubject, isComplete]);

    // error handling effect
    React.useEffect(() => {
        if (taskId && folderLoadState === 'Loaded' && !taskFolder) {
            setErrorType(AssetErrorType.NotFound);
        } else if (taskId && loadState === 'Loaded' && !task) {
            setErrorType(AssetErrorType.NotFound);
        } else if (taskId && loadState === 'Error' && !task) {
            setErrorType(AssetErrorType.Retryable);
        }
    }, [taskId, parentFolderId, loadState, folderLoadState, task, taskFolder]);

    const retryCallback = React.useCallback(() => {
        initializeTasks(projectId);
    }, [projectId]);

    return {
        taskId: taskId,
        taskSubject: task ? task.Subject : taskSubject ?? '',
        subtasks: task?.Subtasks,
        folderId: parentFolderId,
        isTaskComplete: task ? isTaskComplete(task.Status) : isComplete ?? false,
        errorType: errorType,
        hasLocalTaskSubject: hasLocalTaskSubject,
        disableSubtasks: shouldDisableSubtasks,
        retryCallback,
    };
}
